import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Button,
  IconButton,
  Paper,
  Tooltip,
  Typography,
} from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import PropertyNicknameChip from './PropertyNicknameChip';
import { detailLineCopyText } from './HouseholdDetailLine';
import {
  StayCalendarBooking,
  CalendarBarSourceKind,
  TodayStayItem,
  addDays,
  addMonths,
  buildMonthSegments,
  buildPropertyStayLanesForRange,
  calendarBarSourceLabel,
  monthLabel,
  offsetForDate,
  resolveTimelineWindow,
  startOfMonth,
  summarizeStaysForDay,
  ymdKey,
} from '../utils/bookingsStayCalendar';
import { isCancelledBookingStatus } from '../utils/bookingStatus';
import { resolveBookingPlatformLink } from '../utils/bookingPlatformUrl';

const LABEL_WIDTH = 96;
const DAY_WIDTH = 28;
const BAR_HEIGHT = 22;
const BAR_GAP = 4;
const LANE_PAD_Y = 6;
const HEADER_STICKY_TOP = 0;

const SOURCE_LEGEND: CalendarBarSourceKind[] = ['ical', 'export', 'manual', 'merged'];

/** Solid fills by source — property is already the row, so colour encodes source only. */
export const CALENDAR_SOURCE_COLOURS: Record<
  CalendarBarSourceKind,
  { bg: string; fg: string; border: string }
> = {
  ical: { bg: '#4FC3F7', fg: '#014361', border: '#0288D1' },
  export: { bg: '#FFB74D', fg: '#7A4100', border: '#F57C00' },
  manual: { bg: '#B0BEC5', fg: '#263238', border: '#607D8B' },
  merged: { bg: '#81C784', fg: '#1B5E20', border: '#43A047' },
  other: { bg: '#E0E0E0', fg: '#424242', border: '#9E9E9E' },
};

/** Cancelled stays: deep red + diagonal hatch (pattern helps colour-blind readers). */
export const CALENDAR_CANCELLED_COLOUR = {
  bg: '#8B0000',
  fg: '#FFEBEE',
  border: '#5C0000',
  /** Light hatch over the deep fill — readable without relying on red alone. */
  hatch: `repeating-linear-gradient(
    -45deg,
    rgba(255, 255, 255, 0.28) 0px,
    rgba(255, 255, 255, 0.28) 3px,
    transparent 3px,
    transparent 8px
  )`,
};

function sourceBarSx(kind: CalendarBarSourceKind): Record<string, unknown> {
  const c = CALENDAR_SOURCE_COLOURS[kind] || CALENDAR_SOURCE_COLOURS.other;
  return {
    bgcolor: c.bg,
    color: c.fg,
    border: `1px solid ${c.border}`,
    backgroundImage: 'none',
    boxShadow: 'none',
  };
}

function stayBarSx(kind: CalendarBarSourceKind, cancelled: boolean): Record<string, unknown> {
  if (cancelled) {
    const c = CALENDAR_CANCELLED_COLOUR;
    return {
      bgcolor: c.bg,
      color: c.fg,
      border: `1px solid ${c.border}`,
      backgroundImage: c.hatch,
      backgroundColor: c.bg,
      boxShadow: 'none',
      textDecoration: 'line-through',
    };
  }
  return sourceBarSx(kind);
}

function formatTodayPanelDate(dayYmd: string): string {
  const d = new Date(`${dayYmd}T12:00:00`);
  if (Number.isNaN(d.getTime())) return dayYmd;
  return d.toLocaleDateString('en-GB', {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
}

function TodayActivityRow({
  item,
  onOpenBooking,
}: {
  item: TodayStayItem;
  onOpenBooking?: (bookingId: number) => void;
}) {
  const cancelled = isCancelledBookingStatus(item.booking.status);
  return (
    <Box
      component={onOpenBooking ? 'button' : 'div'}
      type={onOpenBooking ? 'button' : undefined}
      onClick={onOpenBooking ? () => onOpenBooking(item.bookingId) : undefined}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        width: '100%',
        textAlign: 'left',
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 1,
        bgcolor: 'background.paper',
        px: 1,
        py: 0.75,
        cursor: onOpenBooking ? 'pointer' : 'default',
        '&:hover': onOpenBooking ? { bgcolor: 'action.hover' } : undefined,
      }}
    >
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography variant="body2" fontWeight={600} noWrap>
          {item.guestLabel}
          {item.sameDay ? ' · same-day' : ''}
          {cancelled ? ' · cancelled' : ''}
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mt: 0.25, flexWrap: 'wrap' }}>
          <PropertyNicknameChip nickname={item.propertyNickname} propertyId={item.propertyId} />
          <Typography variant="caption" color="text.secondary">
            {formatHoverDate(item.checkInYmd)} → {formatHoverDate(item.checkOutYmd)}
          </Typography>
        </Box>
      </Box>
      <Box
        sx={{
          px: 0.75,
          py: 0.2,
          borderRadius: 1,
          fontSize: '0.65rem',
          fontWeight: 700,
          flexShrink: 0,
          ...stayBarSx(item.sourceKind, cancelled),
          textDecoration: 'none',
        }}
      >
        {cancelled ? 'Cancelled' : calendarBarSourceLabel(item.sourceKind)}
      </Box>
    </Box>
  );
}

function TodayActivitySection({
  title,
  emptyLabel,
  items,
  onOpenBooking,
}: {
  title: string;
  emptyLabel: string;
  items: TodayStayItem[];
  onOpenBooking?: (bookingId: number) => void;
}) {
  return (
    <Box sx={{ minWidth: 0 }}>
      <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 0.75 }}>
        {title}{' '}
        <Typography component="span" variant="caption" color="text.secondary">
          ({items.length})
        </Typography>
      </Typography>
      {items.length === 0 ? (
        <Typography variant="caption" color="text.disabled">
          {emptyLabel}
        </Typography>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
          {items.map((item) => (
            <TodayActivityRow key={`${item.kind}-${item.bookingId}`} item={item} onOpenBooking={onOpenBooking} />
          ))}
        </Box>
      )}
    </Box>
  );
}

function sourcePrefix(kind: CalendarBarSourceKind): string {
  switch (kind) {
    case 'ical':
      return 'I';
    case 'export':
      return 'E';
    case 'merged':
      return 'M';
    case 'manual':
      return '';
    default:
      return '';
  }
}

function formatHoverDate(value?: string | null): string {
  if (!value) return '—';
  const s = String(value).trim();
  const m = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (m) {
    return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3])).toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });
  }
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
}

function formatHoverDateTime(value?: string | null): string {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return formatHoverDate(value);
  return d.toLocaleString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function formatHoverAmount(amount?: number | null, currency?: string | null): string {
  if (amount == null || !Number.isFinite(Number(amount))) return '—';
  const cur = String(currency || 'EUR').toUpperCase();
  try {
    return new Intl.NumberFormat('en-GB', { style: 'currency', currency: cur }).format(Number(amount));
  } catch {
    return `${Number(amount).toFixed(2)} ${cur}`;
  }
}

async function writeClipboardText(text: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    try {
      const ta = document.createElement('textarea');
      ta.value = text;
      ta.setAttribute('readonly', '');
      ta.style.position = 'fixed';
      ta.style.left = '-9999px';
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
      return true;
    } catch {
      return false;
    }
  }
}

function HoverDetailRow({ label, value }: { label: string; value: React.ReactNode }) {
  const copyText = detailLineCopyText(value);
  const empty = value == null || value === '' || value === '—';
  const [copied, setCopied] = useState(false);
  const timerRef = useRef<number | null>(null);

  useEffect(
    () => () => {
      if (timerRef.current != null) window.clearTimeout(timerRef.current);
    },
    []
  );

  const onCopy = async (event: React.MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();
    if (!copyText) return;
    const ok = await writeClipboardText(copyText);
    if (!ok) return;
    setCopied(true);
    if (timerRef.current != null) window.clearTimeout(timerRef.current);
    timerRef.current = window.setTimeout(() => setCopied(false), 1600);
  };

  return (
    <Box sx={{ display: 'grid', gridTemplateColumns: '110px 1fr', gap: 1, py: 0.35, alignItems: 'flex-start' }}>
      <Typography variant="caption" color="text.secondary" sx={{ pt: 0.15 }}>
        {label}
      </Typography>
      <Box sx={{ display: 'inline-flex', alignItems: 'flex-start', gap: 0.15, minWidth: 0 }}>
        <Typography
          variant="caption"
          color={empty ? 'text.disabled' : undefined}
          sx={{ fontWeight: empty ? 400 : 600, wordBreak: 'break-word', pt: 0.15, flex: 1, minWidth: 0 }}
        >
          {empty ? '—' : value}
        </Typography>
        <Tooltip title={copied ? 'Copied' : `Copy ${label}`}>
          <span>
            <IconButton
              size="small"
              aria-label={`Copy ${label}`}
              data-testid={`copy-stay-hover-${label}`}
              disabled={!copyText}
              onClick={onCopy}
              onMouseDown={(e) => e.stopPropagation()}
              sx={{ p: 0.25, mt: -0.2 }}
            >
              {copied ? <CheckIcon sx={{ fontSize: 14 }} /> : <ContentCopyIcon sx={{ fontSize: 14 }} />}
            </IconButton>
          </span>
        </Tooltip>
      </Box>
    </Box>
  );
}

/** Plain-text summary for the booking hover card clipboard button. */
export function buildStayHoverCardCopyText(input: {
  guestLabel: string;
  idLabel: string;
  platform?: string | null;
  sourceLabel: string;
  propertyNickname: string;
  bookingRef?: string | null;
  stayLabel: string;
  guestsLabel?: string | null;
  amountLabel?: string | null;
  bookedLabel?: string | null;
  lastUpdatedLabel?: string | null;
  status?: string | null;
  linkedIdsLabel?: string | null;
  platformUrl?: string | null;
}): string {
  const lines: string[] = [];
  const push = (label: string, value?: string | null) => {
    const text = (value || '').trim();
    if (!text || text === '—') return;
    lines.push(`${label}: ${text}`);
  };
  push('Guest', input.guestLabel);
  push(
    'Booking',
    [input.idLabel, input.platform, input.sourceLabel].filter(Boolean).join(' · ')
  );
  push('Property', input.propertyNickname);
  push('Airbnb ref', input.bookingRef);
  push('Stay', input.stayLabel);
  push('Guests', input.guestsLabel);
  push('Amount', input.amountLabel);
  push('Date booked', input.bookedLabel);
  push('Last updated', input.lastUpdatedLabel);
  push('Status', input.status);
  push('Linked IDs', input.linkedIdsLabel);
  push('Platform URL', input.platformUrl);
  return lines.join('\n');
}

function StayHoverCard({
  bar,
  propertyNickname,
}: {
  bar: {
    guestLabel: string;
    sourceKind: CalendarBarSourceKind;
    sourceLabel: string;
    checkInYmd: string;
    checkOutYmd: string;
    booking: StayCalendarBooking;
  };
  propertyNickname: string;
}) {
  const b = bar.booking;
  const prefix = sourcePrefix(bar.sourceKind);
  const idLabel = prefix ? `${prefix} ${b.id}` : String(b.id);
  const booked =
    b.booked_at_display || b.booked_at_fallback || b.booked_at || null;
  const lastUpdated = b.last_sync || b.updated_at || b.created_at || null;
  const amount = b.total_payout_amount ?? b.host_payout_amount;
  const nights =
    b.nights != null && Number.isFinite(Number(b.nights))
      ? Number(b.nights)
      : null;
  const guestsBits = [
    b.adults_count != null ? `${b.adults_count} adult${Number(b.adults_count) === 1 ? '' : 's'}` : null,
    b.children_count != null && Number(b.children_count) > 0
      ? `${b.children_count} child${Number(b.children_count) === 1 ? '' : 'ren'}`
      : null,
  ].filter(Boolean);
  const airbnbUrl = resolveBookingPlatformLink(b);
  const cancelled = isCancelledBookingStatus(b.status);
  const stayLabel = `${formatHoverDate(bar.checkInYmd)} → ${formatHoverDate(bar.checkOutYmd)}${
    nights != null ? ` · ${nights}n` : ''
  }`;
  const linkedIdsLabel =
    b.is_merged_pair && b.linked_ical_booking_id != null && b.linked_manual_booking_id != null
      ? `I ${b.linked_ical_booking_id} · E/M ${b.linked_manual_booking_id}`
      : null;
  const copyAllText = buildStayHoverCardCopyText({
    guestLabel: bar.guestLabel,
    idLabel,
    platform: b.platform,
    sourceLabel: cancelled ? 'Cancelled' : bar.sourceLabel,
    propertyNickname,
    bookingRef: b.booking_ref || null,
    stayLabel,
    guestsLabel: guestsBits.length ? guestsBits.join(', ') : null,
    amountLabel: formatHoverAmount(amount, b.currency),
    bookedLabel: formatHoverDate(booked),
    lastUpdatedLabel: formatHoverDateTime(lastUpdated),
    status: b.status || null,
    linkedIdsLabel,
    platformUrl: airbnbUrl?.url || null,
  });
  const [copiedAll, setCopiedAll] = useState(false);
  const copiedAllTimerRef = useRef<number | null>(null);

  useEffect(
    () => () => {
      if (copiedAllTimerRef.current != null) window.clearTimeout(copiedAllTimerRef.current);
    },
    []
  );

  const onCopyAll = async (event: React.MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();
    if (!copyAllText) return;
    const ok = await writeClipboardText(copyAllText);
    if (!ok) return;
    setCopiedAll(true);
    if (copiedAllTimerRef.current != null) window.clearTimeout(copiedAllTimerRef.current);
    copiedAllTimerRef.current = window.setTimeout(() => setCopiedAll(false), 1600);
  };

  return (
    <Box sx={{ minWidth: 260, maxWidth: 320, p: 1.25 }}>
      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1, mb: 1 }}>
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="subtitle2" fontWeight={700} noWrap title={bar.guestLabel}>
            {bar.guestLabel}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {idLabel}
            {b.platform ? ` · ${b.platform}` : ''}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25, flexShrink: 0 }}>
          <Tooltip title={copiedAll ? 'Copied' : 'Copy booking details'}>
            <span>
              <IconButton
                size="small"
                aria-label="Copy booking details"
                data-testid="copy-stay-hover-all"
                disabled={!copyAllText}
                onClick={onCopyAll}
                onMouseDown={(e) => e.stopPropagation()}
                sx={{ p: 0.35 }}
              >
                {copiedAll ? <CheckIcon sx={{ fontSize: 16 }} /> : <ContentCopyIcon sx={{ fontSize: 16 }} />}
              </IconButton>
            </span>
          </Tooltip>
          <Box
            sx={{
              px: 0.75,
              py: 0.25,
              borderRadius: 1,
              fontSize: '0.65rem',
              fontWeight: 700,
              ...stayBarSx(bar.sourceKind, cancelled),
              textDecoration: 'none',
              lineHeight: 1.4,
            }}
          >
            {cancelled ? 'Cancelled' : bar.sourceLabel}
          </Box>
        </Box>
      </Box>

      <Box sx={{ mb: 1 }}>
        <PropertyNicknameChip nickname={propertyNickname} propertyId={b.property_id} />
      </Box>

      <Box
        sx={{
          borderTop: '1px solid',
          borderColor: 'divider',
          pt: 0.5,
        }}
      >
        <HoverDetailRow label="Airbnb ref" value={b.booking_ref || null} />
        <HoverDetailRow label="Stay" value={stayLabel} />
        <HoverDetailRow label="Guests" value={guestsBits.length ? guestsBits.join(', ') : null} />
        <HoverDetailRow label="Amount" value={formatHoverAmount(amount, b.currency)} />
        <HoverDetailRow label="Date booked" value={formatHoverDate(booked)} />
        <HoverDetailRow label="Last updated" value={formatHoverDateTime(lastUpdated)} />
        <HoverDetailRow label="Status" value={b.status || null} />
        {linkedIdsLabel ? <HoverDetailRow label="Linked IDs" value={linkedIdsLabel} /> : null}
      </Box>

      {airbnbUrl ? (
        <Box sx={{ mt: 1.25, pt: 1, borderTop: '1px solid', borderColor: 'divider' }}>
          <Button
            component="a"
            href={airbnbUrl.url}
            target="_blank"
            rel="noopener noreferrer"
            size="small"
            variant="contained"
            fullWidth
            endIcon={<OpenInNewIcon sx={{ fontSize: '0.95rem !important' }} />}
            onClick={(e) => e.stopPropagation()}
          >
            {`Open on ${airbnbUrl.platformLabel}`}
          </Button>
        </Box>
      ) : null}
    </Box>
  );
}

type BookingsStayCalendarProps = {
  bookings: StayCalendarBooking[];
  onOpenBooking?: (bookingId: number) => void;
};

const BookingsStayCalendar: React.FC<BookingsStayCalendarProps> = ({
  bookings,
  onOpenBooking,
}) => {
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const didInitialScroll = useRef(false);
  const [visibleMonthLabel, setVisibleMonthLabel] = useState(() => monthLabel(startOfMonth(new Date())));

  const timeline = useMemo(() => resolveTimelineWindow(bookings), [bookings]);
  const { rangeStart, dayCount } = timeline;
  const segments = useMemo(() => buildMonthSegments(rangeStart, dayCount), [rangeStart, dayCount]);
  const lanes = useMemo(
    () => buildPropertyStayLanesForRange(bookings, rangeStart, dayCount),
    [bookings, rangeStart, dayCount]
  );
  const todayKey = ymdKey(new Date());
  const todayOffset = offsetForDate(rangeStart, new Date());
  const gridWidth = dayCount * DAY_WIDTH;

  const updateVisibleMonth = useCallback(() => {
    const el = scrollRef.current;
    if (!el || segments.length === 0) return;
    const centerOffset = Math.floor((el.scrollLeft + el.clientWidth / 2) / DAY_WIDTH);
    let label = segments[0].label;
    for (let i = 0; i < segments.length; i += 1) {
      const seg = segments[i];
      if (centerOffset >= seg.startOffset && centerOffset < seg.startOffset + seg.dayCount) {
        label = seg.label;
        break;
      }
      if (centerOffset >= seg.startOffset) label = seg.label;
    }
    setVisibleMonthLabel(label);
  }, [segments]);

  const scrollByMonths = (deltaMonths: number) => {
    const el = scrollRef.current;
    if (!el) return;
    const centerDay = addDays(rangeStart, Math.floor((el.scrollLeft + el.clientWidth / 2) / DAY_WIDTH));
    const targetMonth = addMonths(startOfMonth(centerDay), deltaMonths);
    const offset = Math.max(0, offsetForDate(rangeStart, targetMonth));
    el.scrollTo({ left: offset * DAY_WIDTH, behavior: 'smooth' });
  };

  const scrollToToday = () => {
    const el = scrollRef.current;
    if (!el) return;
    const left = Math.max(0, todayOffset * DAY_WIDTH - el.clientWidth / 3);
    el.scrollTo({ left, behavior: 'smooth' });
  };

  useEffect(() => {
    didInitialScroll.current = false;
  }, [dayCount, rangeStart]);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    updateVisibleMonth();
    if (!didInitialScroll.current) {
      const left = Math.max(0, todayOffset * DAY_WIDTH - el.clientWidth / 3);
      el.scrollLeft = left;
      didInitialScroll.current = true;
      updateVisibleMonth();
    }
  }, [todayOffset, updateVisibleMonth, dayCount, rangeStart]);

  const dayHeaders = useMemo(() => {
    const weekdayShort = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'] as const;
    const out: Array<{
      key: string;
      offset: number;
      day: number;
      weekday: string;
      isToday: boolean;
      isWeekend: boolean;
      isMonthStart: boolean;
    }> = [];
    for (let i = 0; i < dayCount; i += 1) {
      const d = addDays(rangeStart, i);
      const key = ymdKey(d);
      const weekday = d.getDay();
      out.push({
        key,
        offset: i,
        day: d.getDate(),
        weekday: weekdayShort[weekday],
        isToday: key === todayKey,
        isWeekend: weekday === 0 || weekday === 6,
        isMonthStart: d.getDate() === 1,
      });
    }
    return out;
  }, [rangeStart, dayCount, todayKey]);

  const weekendOffsets = useMemo(
    () => dayHeaders.filter((d) => d.isWeekend).map((d) => d.offset),
    [dayHeaders]
  );

  const todaySummary = useMemo(
    () => summarizeStaysForDay(bookings, todayKey),
    [bookings, todayKey]
  );
  const todayBusyCount =
    todaySummary.checkIns.length + todaySummary.checkOuts.length + todaySummary.ongoing.length;

  return (
    <Paper variant="outlined" sx={{ p: 1.5, mb: 1 }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 1,
          mb: 1.5,
          flexWrap: 'wrap',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
          <IconButton size="small" aria-label="Scroll to previous month" onClick={() => scrollByMonths(-1)}>
            <ChevronLeftIcon />
          </IconButton>
          <Typography variant="subtitle1" fontWeight={600} sx={{ minWidth: 160, textAlign: 'center' }}>
            {visibleMonthLabel}
          </Typography>
          <IconButton size="small" aria-label="Scroll to next month" onClick={() => scrollByMonths(1)}>
            <ChevronRightIcon />
          </IconButton>
          <Button
            size="small"
            variant="contained"
            color="primary"
            onClick={scrollToToday}
            disabled={todayOffset < 0 || todayOffset >= dayCount}
            sx={{ ml: 0.5 }}
          >
            Today
          </Button>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          <Typography variant="caption" color="text.secondary">
            Scroll sideways for other months
          </Typography>
        </Box>
      </Box>

      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          gap: 1.5,
          mb: 1.25,
        }}
      >
        <Typography variant="caption" color="text.secondary">
          Source:
        </Typography>
        {SOURCE_LEGEND.map((kind) => {
          const demo = sourceBarSx(kind);
          return (
            <Box key={kind} sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
              <Box
                sx={{
                  width: 28,
                  height: 12,
                  borderRadius: 0.5,
                  ...demo,
                }}
              />
              <Typography variant="caption" color="text.secondary">
                {calendarBarSourceLabel(kind)}
              </Typography>
            </Box>
          );
        })}
        <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
          <Box
            sx={{
              width: 28,
              height: 12,
              borderRadius: 0.5,
              ...stayBarSx('other', true),
              textDecoration: 'none',
            }}
          />
          <Typography variant="caption" color="text.secondary">
            Cancelled
          </Typography>
        </Box>
      </Box>
      {lanes.length === 0 ? (
        <Typography variant="body2" color="text.secondary" sx={{ py: 3, textAlign: 'center' }}>
          No stays in the visible timeline for the current filters.
        </Typography>
      ) : (
        <Box sx={{ display: 'flex', width: '100%', alignItems: 'stretch' }}>
          {/* Sticky property labels */}
          <Box
            sx={{
              width: LABEL_WIDTH,
              flexShrink: 0,
              borderRight: '1px solid',
              borderColor: 'divider',
              bgcolor: 'background.paper',
              zIndex: 2,
            }}
          >
            <Box sx={{ height: 44, borderBottom: '1px solid', borderColor: 'divider' }} />
            <Box sx={{ height: 36, borderBottom: '1px solid', borderColor: 'divider' }} />
            {lanes.map((lane) => {
              const rowHeight =
                LANE_PAD_Y * 2 + lane.stackCount * BAR_HEIGHT + (lane.stackCount - 1) * BAR_GAP;
              return (
                <Box
                  key={lane.propertyKey}
                  sx={{
                    height: Math.max(40, rowHeight),
                    display: 'flex',
                    alignItems: 'center',
                    pr: 1,
                    borderTop: '2px solid',
                    borderColor: 'grey.500',
                  }}
                >
                  <PropertyNicknameChip nickname={lane.nickname} propertyId={lane.propertyId} />
                </Box>
              );
            })}
          </Box>

          <Box
            ref={scrollRef}
            onScroll={updateVisibleMonth}
            sx={{
              flex: 1,
              overflowX: 'auto',
              overflowY: 'hidden',
              WebkitOverflowScrolling: 'touch',
            }}
          >
            <Box sx={{ width: gridWidth, position: 'relative' }}>
              {/* Month band */}
              <Box
                sx={{
                  display: 'flex',
                  height: 44,
                  position: 'sticky',
                  top: HEADER_STICKY_TOP,
                  zIndex: 1,
                  bgcolor: 'background.paper',
                  borderBottom: '1px solid',
                  borderColor: 'divider',
                }}
              >
                {segments.map((seg) => (
                  <Box
                    key={seg.key}
                    sx={{
                      width: seg.dayCount * DAY_WIDTH,
                      flexShrink: 0,
                      px: 0.75,
                      display: 'flex',
                      alignItems: 'center',
                      borderLeft: '1px solid',
                      borderColor: 'divider',
                      bgcolor: 'action.hover',
                    }}
                  >
                    <Typography variant="caption" fontWeight={700} noWrap>
                      {seg.shortLabel}
                    </Typography>
                  </Box>
                ))}
              </Box>

              {/* Weekday + day numbers */}
              <Box sx={{ display: 'flex', height: 36, borderBottom: '1px solid', borderColor: 'divider' }}>
                {dayHeaders.map((d) => (
                  <Box
                    key={d.key}
                    sx={{
                      width: DAY_WIDTH,
                      flexShrink: 0,
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      lineHeight: 1.1,
                      borderLeft: d.isMonthStart ? '2px solid' : '1px solid',
                      borderColor: d.isMonthStart ? 'text.disabled' : 'divider',
                      bgcolor: d.isToday
                        ? 'rgba(25, 118, 210, 0.22)'
                        : d.isWeekend
                          ? 'rgba(84, 110, 122, 0.18)'
                          : 'transparent',
                      boxShadow: d.isToday
                        ? 'inset 0 -2px 0 #1976d2'
                        : d.isWeekend
                          ? 'inset 0 -2px 0 #78909C'
                          : undefined,
                    }}
                  >
                    <Typography
                      variant="caption"
                      sx={{
                        fontWeight: d.isWeekend ? 700 : 600,
                        color: d.isToday
                          ? 'primary.main'
                          : d.isWeekend
                            ? '#455A64'
                            : 'text.secondary',
                        fontSize: '0.55rem',
                        lineHeight: 1.1,
                      }}
                    >
                      {d.weekday}
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{
                        fontWeight: d.isToday || d.isWeekend ? 700 : 500,
                        color: d.isToday
                          ? 'primary.main'
                          : d.isWeekend
                            ? 'text.primary'
                            : 'text.secondary',
                        fontSize: '0.65rem',
                        lineHeight: 1.15,
                      }}
                    >
                      {d.day}
                    </Typography>
                  </Box>
                ))}
              </Box>

              {lanes.map((lane) => {
                const rowHeight =
                  LANE_PAD_Y * 2 + lane.stackCount * BAR_HEIGHT + (lane.stackCount - 1) * BAR_GAP;
                return (
                  <Box
                    key={lane.propertyKey}
                    sx={{
                      position: 'relative',
                      height: Math.max(40, rowHeight),
                      borderTop: '2px solid',
                      borderColor: 'grey.500',
                      backgroundImage: `repeating-linear-gradient(to right, transparent 0, transparent ${
                        DAY_WIDTH - 1
                      }px, rgba(0,0,0,0.06) ${DAY_WIDTH - 1}px, rgba(0,0,0,0.06) ${DAY_WIDTH}px)`,
                    }}
                  >
                    {weekendOffsets.map((offset) => (
                      <Box
                        key={`weekend-${lane.propertyKey}-${offset}`}
                        aria-hidden
                        sx={{
                          position: 'absolute',
                          left: offset * DAY_WIDTH,
                          width: DAY_WIDTH,
                          top: 0,
                          bottom: 0,
                          bgcolor: 'rgba(84, 110, 122, 0.12)',
                          pointerEvents: 'none',
                          zIndex: 0,
                        }}
                      />
                    ))}
                    {todayOffset >= 0 && todayOffset < dayCount ? (
                      <Box
                        key={`today-${lane.propertyKey}`}
                        aria-hidden
                        sx={{
                          position: 'absolute',
                          left: todayOffset * DAY_WIDTH,
                          width: DAY_WIDTH,
                          top: 0,
                          bottom: 0,
                          bgcolor: 'rgba(25, 118, 210, 0.16)',
                          boxShadow: 'inset 0 0 0 1px rgba(25, 118, 210, 0.35)',
                          pointerEvents: 'none',
                          zIndex: 0,
                        }}
                      />
                    ) : null}
                    {lane.bars.map((bar) => {
                      const left = bar.startOffset * DAY_WIDTH + 1;
                      const width = Math.max(
                        DAY_WIDTH - 2,
                        (bar.endOffsetExclusive - bar.startOffset) * DAY_WIDTH - 2
                      );
                      const prefix = sourcePrefix(bar.sourceKind);
                      const label = prefix ? `${prefix} ${bar.guestLabel}` : bar.guestLabel;
                      const cancelled = isCancelledBookingStatus(bar.booking.status);
                      return (
                        <Tooltip
                          key={`${bar.bookingId}-${bar.startOffset}`}
                          enterDelay={250}
                          leaveDelay={280}
                          placement="top"
                          arrow
                          describeChild
                          disableInteractive={false}
                          slotProps={{
                            tooltip: {
                              sx: {
                                bgcolor: 'background.paper',
                                color: 'text.primary',
                                border: '1px solid',
                                borderColor: 'divider',
                                boxShadow: 6,
                                p: 0,
                                maxWidth: 'none',
                                borderRadius: 1.5,
                              },
                            },
                            arrow: {
                              sx: {
                                color: 'background.paper',
                                '&::before': {
                                  border: '1px solid',
                                  borderColor: 'divider',
                                  boxSizing: 'border-box',
                                },
                              },
                            },
                          }}
                          title={<StayHoverCard bar={bar} propertyNickname={lane.nickname} />}
                        >
                          <Box
                            component="button"
                            type="button"
                            onClick={() => onOpenBooking?.(bar.bookingId)}
                            aria-label={bar.title}
                            sx={{
                              position: 'absolute',
                              left,
                              width,
                              top: LANE_PAD_Y + bar.stack * (BAR_HEIGHT + BAR_GAP),
                              height: BAR_HEIGHT,
                              borderRadius: 1,
                              px: 0.75,
                              cursor: onOpenBooking ? 'pointer' : 'default',
                              fontSize: '0.7rem',
                              fontWeight: 600,
                              lineHeight: `${BAR_HEIGHT - 2}px`,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                              textAlign: 'left',
                              zIndex: 1,
                              borderTopLeftRadius: bar.clippedStart ? 0 : undefined,
                              borderBottomLeftRadius: bar.clippedStart ? 0 : undefined,
                              borderTopRightRadius: bar.clippedEnd ? 0 : undefined,
                              borderBottomRightRadius: bar.clippedEnd ? 0 : undefined,
                              ...stayBarSx(bar.sourceKind, cancelled),
                              '&:hover': { filter: 'brightness(0.96)' },
                            }}
                          >
                            {label}
                          </Box>
                        </Tooltip>
                      );
                    })}
                  </Box>
                );
              })}
            </Box>
          </Box>
        </Box>
      )}

      <Box
        sx={{
          mt: 1.5,
          p: 1.5,
          borderRadius: 1.5,
          border: '1px solid',
          borderColor: 'primary.light',
          bgcolor: 'rgba(25, 118, 210, 0.06)',
          boxShadow: 'inset 3px 0 0 #1976d2',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'baseline',
            justifyContent: 'space-between',
            gap: 1,
            flexWrap: 'wrap',
            mb: 1,
          }}
        >
          <Box>
            <Typography variant="subtitle1" fontWeight={700} color="primary.main">
              Today
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {formatTodayPanelDate(todayKey)}
              {todayBusyCount === 0
                ? ' · nothing scheduled in the current filters'
                : ` · ${todaySummary.checkIns.length} check-in${
                    todaySummary.checkIns.length === 1 ? '' : 's'
                  }, ${todaySummary.checkOuts.length} check-out${
                    todaySummary.checkOuts.length === 1 ? '' : 's'
                  }, ${todaySummary.ongoing.length} ongoing`}
            </Typography>
          </Box>
          <Button size="small" variant="outlined" onClick={scrollToToday} disabled={todayOffset < 0 || todayOffset >= dayCount}>
            Jump to column
          </Button>
        </Box>

        {todaySummary.turnoverPropertyKeys.length > 0 ? (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
            Turnover today:{' '}
            <Box component="span" sx={{ fontWeight: 700, color: 'text.primary' }}>
              {todaySummary.turnoverPropertyKeys.join(', ')}
            </Box>
          </Typography>
        ) : null}

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: 'repeat(3, minmax(0, 1fr))' },
            gap: 1.5,
          }}
        >
          <TodayActivitySection
            title="Check-ins"
            emptyLabel="No check-ins today"
            items={todaySummary.checkIns}
            onOpenBooking={onOpenBooking}
          />
          <TodayActivitySection
            title="Check-outs"
            emptyLabel="No check-outs today"
            items={todaySummary.checkOuts}
            onOpenBooking={onOpenBooking}
          />
          <TodayActivitySection
            title="Ongoing stays"
            emptyLabel="No guests in house"
            items={todaySummary.ongoing}
            onOpenBooking={onOpenBooking}
          />
        </Box>
      </Box>
    </Paper>
  );
};

export default BookingsStayCalendar;
